import { useState, useCallback, useEffect } from 'react'
import { Users, Plus, Search, Filter, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Loading } from '@/components/ui/loading'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEmployees, useDepartments, useLevels, useDeleteEmployee } from '@/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { EmployeeForm } from './EmployeeForm'
import { WorkHistoryPanel } from './WorkHistoryPanel'
import { Employee, EmployeeStatus, Gender } from '@/types'
import { cn } from '@/utils'
import { toast } from 'sonner'

const statusMap: Record<EmployeeStatus, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  '正式': { label: '正式', variant: 'success' },
  '离职': { label: '离职', variant: 'secondary' },
  '试用期': { label: '试用期', variant: 'warning' },
}

const genderMap: Record<Gender, string> = {
  '男': '男',
  '女': '女',
}

const deptNameMap: Record<string, string> = {
  'TECH': '技术部',
  'HR': '人事部',
  'FIN': '财务部',
  'MKT': '市场部',
}

export function EmployeesPage() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [deptFilter, setDeptFilter] = useState<string | null>(null)
  const [levelFilter, setLevelFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)

  const { data: employeesData, isLoading } = useEmployees({
    page,
    page_size: pageSize,
    dept_code: deptFilter,
    level_code: levelFilter,
    status: statusFilter,
    search: debouncedSearch || undefined,
  })

  // Handle search input
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
  }, [])

  const { data: departments } = useDepartments()
  const { data: levels } = useLevels()
  const deleteEmployee = useDeleteEmployee()

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [deptFilter, levelFilter, statusFilter, debouncedSearch])

  const handleDelete = useCallback(async (employee: Employee) => {
    if (!confirm(`确定要删除员工 ${employee.name} (${employee.id}) 吗？`)) return
    try {
      await deleteEmployee.mutateAsync(employee.id)
      toast.success(`员工 ${employee.name} 已删除`)
      // useDeleteEmployee hook already invalidates queries in onSuccess
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败')
    }
  }, [deleteEmployee])

  const handleEdit = useCallback((employee: Employee) => {
    setEditingEmployee(employee)
    setIsFormOpen(true)
  }, [])

  const handleViewHistory = useCallback((employee: Employee) => {
    setSelectedEmployee(employee)
  }, [])

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false)
    setEditingEmployee(null)
  }, [])

  const handleFormSuccess = useCallback(() => {
    handleCloseForm()
    queryClient.invalidateQueries({ queryKey: ['employees'] })
  }, [handleCloseForm, queryClient])

  const totalPages = employeesData?.total ? Math.ceil(employeesData.total / pageSize) : 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="w-6 h-6 text-green-500" />
            员工管理
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            员工信息管理、职级和部门配置
          </p>
        </div>
        <Button
          className="gap-2"
          onClick={() => setIsFormOpen(true)}
        >
          <Plus className="w-4 h-4" />
          添加员工
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="搜索员工姓名或工号..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={deptFilter || 'all'} onValueChange={(v) => setDeptFilter(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[160px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="部门" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部部门</SelectItem>
                {departments?.map((dept) => (
                  <SelectItem key={dept.code} value={dept.code}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={levelFilter || 'all'} onValueChange={(v) => setLevelFilter(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="职级" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部职级</SelectItem>
                {levels?.map((level) => (
                  <SelectItem key={level.level_code} value={level.level_code}>
                    {level.level_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="状态" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部状态</SelectItem>
                <SelectItem value="正式">正式</SelectItem>
                <SelectItem value="离职">离职</SelectItem>
                <SelectItem value="试用期">试用期</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card>
        <CardHeader>
          <CardTitle>员工列表</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">工号</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">姓名</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">性别</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">部门</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">职级</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">状态</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">入职日期</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">联系方式</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  // Skeleton loading rows
                  Array.from({ length: pageSize }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="border-b border-slate-100 dark:border-slate-800">
                      <td className="py-3 px-4"><div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-14 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-28 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                      <td className="py-3 px-4"><div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" /></td>
                    </tr>
                  ))
                ) : (employeesData?.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-slate-500">
                      暂无员工数据
                    </td>
                  </tr>
                ) : (
                  employeesData?.items?.map((employee) => (
                    <tr
                      key={employee.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50"
                    >
                      <td className="py-3 px-4 text-sm font-mono text-slate-600 dark:text-slate-300">
                        {employee.id}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900 dark:text-white">
                        {employee.name}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {employee.gender ? genderMap[employee.gender as Gender] : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {deptNameMap[employee.dept_code] || employee.dept_code}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {employee.level_code}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={statusMap[employee.status as EmployeeStatus]?.variant || 'secondary'}>
                          {statusMap[employee.status as EmployeeStatus]?.label || employee.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                        {employee.entry_date}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {employee.phone && <p className="text-slate-600 dark:text-slate-300">{employee.phone}</p>}
                          {employee.email && <p className="text-slate-400">{employee.email}</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewHistory(employee)}
                          >
                            查看
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(employee)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(employee)}
                            className="text-rose-600 hover:text-rose-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-slate-500">
                共 {employeesData?.total || 0} 条记录，第 {page} / {totalPages} 页
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  上一页
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  下一页
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Work History Panel */}
      {selectedEmployee && (
        <WorkHistoryPanel
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}

      {/* Employee Form Dialog */}
      {isFormOpen && (
        <EmployeeForm
          employee={editingEmployee}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  )
}
